import {faBell, faQuestionCircle} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {withTranslation} from 'react-i18next';
import styled from 'styled-components';
import {dateFormatFR, numberFormatThousands} from '../../../utils';
import DataTable from '../../../common/components/Datatable/Datatable';
import Screen from '../../../common/components/layout/Screen';
import Metadata from '../../../common/components/Metadata';
import {FILTER_EXPRESSION_OPERATOR, FILTER_OPERATOR, ORDER_DIRECTION, COLORS} from '../../../common/constants';
import AddCampaignView from './AddCampaignModal';
import {Col, Container, Row} from 'react-bootstrap';
import {flowRight as compose} from 'lodash';
import gql from 'graphql-tag';
import {useQuery, withApollo} from 'react-apollo';
import {PulseLoader} from 'react-spinners';
import CampaignRowAction from './CampaignRowAction';
import DisplayCampaignStatus from './DisplayCampaignStatus';
import Error from '../../../common/components/Error/Error';
import {HeadDiv as DefaultHeadDiv, TitleButton} from '../../../common/components/StyledComp';
import {withRouter} from 'react-router';
import PopIn from '../../../common/components/PopIn';
import Badge from '../../../common/components/Badge';

const GRAPHQL_QUERY_CURRENT_MERCHANT_CAMPAIGNS = gql`
  query currentMerchantCampaigns($criteria:CriteriaInput) {
      campaigns(criteria: $criteria){
        id
        total
        rows{
          id
          status
          title
          content
          link
          startDate
          endDate
          filterUserStartDate
          filterUserEndDate
          filterUserCivility
          totalUsers
          totalUsersWithLoyaltyCard
          totalUsersWithoutLoyaltyCard
          createdAt
          updatedAt
          file {
            id
            token
            filename
            mimeType
          }
        }
      }
    }
`;

const GRAPHQL_QUERY_MERCHANT = gql`
  query currentMerchant{
    currentUser{
      id
      merchant{
        id
        totalAvailableNotification
        totalStartedCampaigns
      }
    }
  }
`;

const Span = styled.span`
  font-family: 'Segoe UI Bold', sans-serif;
`;

const HeadDiv = styled(DefaultHeadDiv)`
  &&& {
    margin-bottom: 0;
    border-radius: 4px;
  }
`;

const BuyNotificationsBtn = styled(TitleButton)`
  margin-left: auto;
  margin-right: 10px;
  height: auto;
  width: auto;
  @media (max-width: 1500px) {
    font-size: 12px;
    margin-right: 2%;
  }
`;

const LinkText = styled.a`
  color: ${COLORS.BLACK};
   &:hover {
    color: ${COLORS.BLACK};
  }
`;

const HelpIconStyle = {
  marginLeft: '10px',
  color: COLORS.BLACK,
};

const columns = t => [
  {
    Header: '#',
    accessor: 'id',
    sortable: true,
  },
  {
    Header: t('datatable.createdAt'),
    Cell: ({cell}) => dateFormatFR({date: cell.value}),
    accessor: 'createdAt',
    sortable: true,
  },
  {
    Header: t('datatable.title'),
    accessor: 'title',
    sortable: true,
  },
  {
    Header: t('datatable.startDate'),
    Cell: ({cell}) => dateFormatFR({date: cell.value}),
    accessor: 'startDate',
    sortable: true,
  },
  {
    Header: t('datatable.endDate'),
    Cell: ({cell}) => dateFormatFR({date: cell.value}),
    accessor: 'endDate',
    sortable: true,
  },
  {
    Header: t('datatable.link'),
    Cell: ({cell}) => cell.value ? cell.value : '',
    accessor: 'link',
    sortable: true,
  },
  {
    Header: t('datatable.totalUsers'),
    accessor: 'totalUsers',
    sortable: true,
  },
  {
    Header: t('datatable.status'),
    Cell: ({cell}) => <DisplayCampaignStatus status={cell.value}/>,
    accessor: 'status',
    sortable: true,
  },
  {
    Header: t('datatable.action'),
    Cell: ({cell}) => <CampaignRowAction campaign={cell.value}/>,
    accessor: row => row,
  },
];

const createFilters = search => {
  const expressions = [{
    operator: FILTER_EXPRESSION_OPERATOR.Or,
    filters: [{
      field: 'campaign.id',
      operator: FILTER_OPERATOR.Like,
      value: `%${search}%`,
    }, {
      field: 'campaign.title',
      operator: FILTER_OPERATOR.Like,
      value: `%${search}%`,
    }, {
      field: 'campaign.link',
      operator: FILTER_OPERATOR.Like,
      value: `%${search}%`,
    }, {
      field: 'campaign.startDate',
      operator: FILTER_OPERATOR.Like,
      value: `%${search}%`,
    }],
  }];

  return {
    expressions,
  };
};

const CampaignsView = ({t, location, history}) => {
  const {error: errorMerchant, loading: loadingMerchant, data: dataMerchant} = useQuery(GRAPHQL_QUERY_MERCHANT, {fetchPolicy: 'cache-and-network'});
  const [search, setSearch] = useState('');

  const searchParams = new URLSearchParams(location.search);
  const [displayCampaignAddedSuccess, setDisplayCampaignAddedSuccess] = useState(searchParams.has('newCampaign'));
  const [displayBuyNotificationsSuccess, setDisplayBuyNotificationsSuccess] = useState(searchParams.has('newNotifications'));

  const onDisplayCampaignAddedSuccess = visible => {
    if (!visible && searchParams.has('newCampaign')) {
      searchParams.delete('newCampaign');
    }
    history.replace(location.pathname + '?' + searchParams.toString() + location.hash);
    setDisplayCampaignAddedSuccess(visible);
  };

  const onDisplayBuyNotificationsSuccess = visible => {
    if (!visible && searchParams.has('newNotifications')) {
      searchParams.delete('newNotifications');
    }
    history.replace(location.pathname + '?' + searchParams.toString() + location.hash);
    setDisplayBuyNotificationsSuccess(visible);
  };

  if (errorMerchant) {
    console.error(errorMerchant);
  }

  const merchant = dataMerchant && dataMerchant.currentUser && dataMerchant.currentUser.merchant ? dataMerchant.currentUser.merchant : null;
  return (
    <Screen
      header={<Metadata meta={t('campaigns.meta')} title={t('campaigns.title')}/>}
      leftTitle={
        <>
          <FontAwesomeIcon icon={faBell} color={'rgb(201,0,95)'}/>
          <Span className="route-name">{t('campaigns.title')}</Span>
        </>
      }
      rightTitle={
        <LinkText href={'https://www.cleanbill.fr/2e418bb561c96e19ca1e080e812d3c30/'} target="_blank" rel="noopener noreferrer">
          {t('campaigns.createCampaignTutorial')}
          <FontAwesomeIcon style={HelpIconStyle} icon={faQuestionCircle} size={'lg'}/>
        </LinkText>
      }
    >
      {errorMerchant ? (
        <Error error={t('unknownError')} />
      ) : (
        <Container className="general-container">
          <Row className="info-row">
            <Col lg={12} xl={6}>
              <Row className="section-info-user">
                <HeadDiv className="info-header-user">
                  {`${t('campaigns.currentActiveCampaigns')} : `}
                  {loadingMerchant ? (
                    <PulseLoader color={'#fff'}/>
                  ) : (
                    <>
                      <Badge className={'ml-1 mr-1'}>{numberFormatThousands(merchant.totalStartedCampaigns)}</Badge>
                      <AddCampaignView merchant={merchant} ButtonComponent={BuyNotificationsBtn}/>
                    </>
                  )}
                </HeadDiv>
              </Row>
            </Col>
            <Col lg={12} className={'d-xl-none'}>
              <br/>
            </Col>
            <Col lg={12} xl={6}>
              <Row className="section-info-user">
                <HeadDiv className="info-header-user">
                  {`${t('availableNotifications')} : `}
                  {loadingMerchant ? <PulseLoader color={'#fff'}/> : <Badge className={'ml-1 mr-1'}>{numberFormatThousands(merchant.totalAvailableNotification)}</Badge>}
                  <BuyNotificationsBtn onClick={() => {history.push('/admin/products/buy/notification');}}>
                    {t('buyNotifications')}
                  </BuyNotificationsBtn>
                </HeadDiv>
              </Row>
            </Col>
          </Row>
        </Container>
      )}
      <DataTable
        query={GRAPHQL_QUERY_CURRENT_MERCHANT_CAMPAIGNS}
        keyResult="campaigns"
        columns={columns(t)}
        defaultSort={{field: 'createdAt', direction: ORDER_DIRECTION.DESC}}
        advancedFilters={createFilters(search)}
        onSearch={value => setSearch(value)}
        fetchPolicy={'cache-and-network'}
      />

      <PopIn title={t('campaigns.titleNewCampaign')} visible={displayCampaignAddedSuccess} setModalVisible={onDisplayCampaignAddedSuccess} size={'md'}>
        {t('campaigns.bodyNewCampaign')}
        <div className="popin-buttons mt-3">
          <TitleButton onClick={e => onDisplayCampaignAddedSuccess(false)}>
            {t('campaigns.ok')}
          </TitleButton>
        </div>
      </PopIn>

      <PopIn title={t('campaigns.titleNewNotifications')} visible={displayBuyNotificationsSuccess} setModalVisible={onDisplayBuyNotificationsSuccess} size={'md'}>
        {t('campaigns.bodyNewNotifications')}
        <div className="popin-buttons mt-3">
          <TitleButton onClick={() => onDisplayBuyNotificationsSuccess(false)}>
            {t('campaigns.ok')}
          </TitleButton>
        </div>
      </PopIn>
    </Screen>
  );
};

CampaignsView.propTypes = {
  t: PropTypes.func.isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired,
  }).isRequired,
};

export default compose(
  withTranslation('campaign'),
  withApollo,
  withRouter,
)(CampaignsView);
